import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, AlertCircle, Info } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { useAnalysisRuns } from '../../lib/queries';

/**
 * Sprint 6 · Validación cruzada del escenario (pre-submit)
 *
 * Reglas puramente frontend, revisadas EN VIVO al cambiar assumptions/structured.
 * No suplanta el `self_check` del motor; sólo advierte antes de correr.
 *
 * Severidad:
 *  - critical: bloquearía coherencia (ej: %s de cobranza ≠ 100%)
 *  - warning: sospechoso pero corridera (ej: crédito sin equity declarado)
 *  - info: hint constructivo (ej: sin plazo de crédito → default 24m)
 */

const near100 = (v) => Math.abs(v - 100) < 0.5;
const pct = (v) => (v || 0) * 100;

// ── SWA-237 · espejo FRONTEND de las alertas de captura del motor ──────────
// Espejo de `AlertEngine._capture_closure_alerts` en
// /app/backend/engine/alerts.py:302-402. Umbrales idénticos, a propósito:
//   · ventas   → max(0.5 uds, 0.5 % del total)  ·  alerts.py:327-328
//   · obra     → 99.5 % POR SERIE                ·  alerts.py:361
//   · cobranza → 99.5 % de las unidades          ·  alerts.py:386
// Si cambias un umbral aquí, cámbialo también allá (y viceversa): son dos
// implementaciones de la misma regla. En alerts.py NO se toca nada.
// Diferencia deliberada de redacción: el semáforo del wizard es PRE-corrida,
// así que habla en futuro ("correrá"); alerts.py habla en pasado porque
// describe una corrida ya hecha.

const parseJsonArray = (raw) => {
  if (typeof raw !== 'string' || !raw.startsWith('[')) return null;
  try {
    const v = JSON.parse(raw);
    return Array.isArray(v) && v.length > 0 ? v : null;
  } catch { return null; }
};

// Espejo de `resolve_sales_mode` · /app/backend/engine/revenue.py:42-54
const resolveSalesMode = (a) => {
  const mode = String(a.sales_capture_mode ?? '').trim().toLowerCase();
  if (mode) return mode;
  return parseJsonArray(a.custom_sales_monthly_json) ? 'manual' : 'general';
};

// Espejo de `resolve_collection_mode` · /app/backend/engine/revenue.py:57-70
const resolveCollectionMode = (a) => {
  const mode = String(a.collection_capture_mode ?? '').trim();
  if (mode) return mode;
  return parseJsonArray(a.collection_segments_json) ? 'segmentada' : 'perfil_unico';
};

function buildCaptureIssues(assumptions = {}, structured = {}) {
  const a = assumptions;
  const out = [];

  // Unidades del proyecto: la key capturada manda (SWA-206); si no existe, la
  // suma de tipologías, que es lo que el motor termina usando.
  // SWA-247B · el campo es `quantity`, como en todo el resto del wizard
  // (steps.jsx:176, :300) y en el payload de GET /structured-inputs. `t.units`
  // no existe: esta suma valía 0 siempre.
  const typoUnits = (structured?.typologies || []).reduce((s, t) => s + (Number(t.quantity) || 0), 0);
  const totalUnits = Number(a.total_units || 0) || typoUnits;

  // ── Ventas · cierra en 100 % de las UNIDADES ── espejo alerts.py:322-344
  if (resolveSalesMode(a) === 'manual') {
    const sales = parseJsonArray(a.custom_sales_monthly_json);
    if (sales && totalUnits > 0) {
      const captured = sales.reduce((s, v) => s + (Number(v) || 0), 0);
      const tol = Math.max(0.5, totalUnits * 0.005);
      if (captured < totalUnits - tol) {
        const p = (captured / totalUnits) * 100;
        out.push({
          severity: 'warning',
          kind: 'captura',
          step: 'sales',
          message: `El programa mensual de ventas acumula ${captured.toFixed(1)} de ${totalUnits} unidades (${p.toFixed(1)} %): quedan ${(totalUnits - captured).toFixed(1)} sin colocar. El análisis correrá con lo capturado, sin repartir el faltante, así que el ingreso y la TIR corresponderán a un proyecto vendido al ${p.toFixed(1)} %.`,
        });
      }
    }
  }

  // ── Obra · cada serie cierra en 100 % de SU partida ── espejo alerts.py:346-376
  if (Number(a.construction_curve_mode || 0) === 1) {
    [['custom_curve_urb_json', 'urbanización'], ['custom_curve_edif_json', 'edificación']].forEach(([key, label]) => {
      const arr = parseJsonArray(a[key]);
      if (!arr) return;
      const total = arr.reduce((s, v) => s + (Number(v) || 0), 0);
      if (total > 0 && total < 99.5) {
        out.push({
          severity: 'warning',
          kind: 'captura',
          step: 'schedule',
          message: `La curva mensual de ${label} suma ${total.toFixed(1)} % del presupuesto de SU partida. El motor ejercerá solo ese ${total.toFixed(1)} %: el costo de la partida quedará ${(100 - total).toFixed(1)} puntos por debajo de lo presupuestado y los indicadores de rentabilidad se calcularán sobre ese gasto menor.`,
        });
      }
    });
  }

  // ── SWA-187 · urbanización + edificación son proporciones del costo duro y suman 100 % ──
  // La captura ya se autocompleta (steps.jsx handleEdifUrbChange); esto cubre valores
  // guardados antes de esa regla. Espejo de la alerta del motor (costs.py split_warning).
  if (!(Number(a.hard_cost_urbanizacion_amount || 0) > 0)) {
    const u = Number(a.hard_cost_urbanizacion_pct ?? 0.30);
    const e = Number(a.hard_cost_edificacion_pct ?? 0.70);
    if (u + e > 0 && Math.abs(u + e - 1) > 0.001) {
      out.push({
        severity: 'warning',
        kind: 'captura',
        step: 'budget',
        message: `Urbanización (${(u * 100).toFixed(1)} %) y edificación (${(e * 100).toFixed(1)} %) suman ${((u + e) * 100).toFixed(1)} % del costo duro. El motor conservará la proporción entre las dos sin cambiar el total; ajústalas para que sumen 100 %.`,
      });
    }
  }

  // ── Cobranza · los canales cierran en 100 % de las unidades ── espejo alerts.py:378-400
  if (resolveCollectionMode(a) === 'segmentada') {
    const segs = parseJsonArray(a.collection_segments_json);
    if (segs) {
      const share = segs.reduce((s, seg) => s + (Number(seg?.pct_units) || 0), 0) * 100;
      if (share > 0 && share < 99.5) {
        out.push({
          severity: 'warning',
          kind: 'captura',
          step: 'collection',
          message: `Los canales de cobranza capturados suman ${share.toFixed(1)} % de las unidades. El ${(100 - share).toFixed(1)} % restante no tiene canal asignado y no generará cobranza en el flujo: el análisis correrá así, sin repartir el faltante.`,
        });
      }
    }
  }

  return out;
}

// SWA-157 · el horizonte no es insumo: es n = len(monthly_schedule) de la corrida.
// Única fuente para la lectura del wizard y para esta validación: la última corrida
// registrada del escenario. Sin corrida devuelve undefined y cada uso cae al piso.
export function useRunHorizon() {
  const { projectId, scenarioId } = useParams();
  const { data: runs } = useAnalysisRuns(projectId);
  const lastRun = (runs || []).find((r) => r.scenario_id === scenarioId && r.status === 'completed');
  return lastRun?.metrics?.project_duration_months;
}

function buildIssues(assumptions = {}, structured = {}, runHorizon) {
  const issues = [];
  const a = assumptions;

  // ── COBRANZA (regla dura) — sólo aplica en perfil único, no en segmentada ──
  // L-60 · `a.collection_mode` no existe: la key es `collection_capture_mode`, y
  // sus valores son 'perfil_unico' / 'segmentada', no 'flat' / 'segmented'. La
  // comparación contra un vocabulario inexistente hacía que la restricción nunca
  // se aplicara y la regla sumara los escalares también en segmentada, donde el
  // motor usa collection_structure (orchestrator.py:302-322) y no estos campos.
  // Se resuelve con el mismo resolutor que el resto del archivo, para que haya
  // una sola fuente de verdad sobre el modo.
  const cobranzaSum =
    pct(a.enganche_pct) +
    pct(a.parcialidades_pct) +
    pct(a.pago_entrega_pct) +
    pct(a.pago_escrituracion_pct);
  if (resolveCollectionMode(a) !== 'segmentada' && cobranzaSum > 0 && !near100(cobranzaSum)) {
    issues.push({
      severity: 'critical',
      step: 'collection',
      message: `Cobranza suma ${cobranzaSum.toFixed(1)}% (debe sumar 100%). Ajusta enganche + parcialidades + entrega + escrituración.`,
    });
  }

  // SWA-280 · la mezcla de compradores (subsidio/hipotecario/contado) está oculta y el motor no la lee:
  // ya no se valida, para no bloquear con un error sobre campos que el usuario no ve.

  // ── PREVENTA ──
  if (pct(a.presale_pct) > 100) {
    issues.push({
      severity: 'critical',
      step: 'sales',
      message: `Preventa está en ${pct(a.presale_pct).toFixed(0)}%. No puede superar 100%.`,
    });
  }

  // ── EQUITY ↔ CRÉDITO ──
  const creditLine = Number(a.credit_line_amount || 0);
  const equityPct = pct(a.equity_pct);
  if (creditLine > 0 && equityPct === 0 && !a.equity_schedule_json) {
    issues.push({
      severity: 'warning',
      step: 'financing',
      message: 'Tienes crédito autorizado pero equity=0%. Define % de recurso propio o un calendario de aportes.',
    });
  }
  if (equityPct > 100) {
    issues.push({
      severity: 'critical',
      step: 'financing',
      message: `Equity está en ${equityPct.toFixed(0)}%. No puede superar 100%.`,
    });
  }

  // ── AFORO / LTV ──
  const aforo = pct(a.credit_aforo_pct);
  const ltv = pct(a.credit_ltv_max);
  if (creditLine > 0 && aforo > 80) {
    issues.push({
      severity: 'warning',
      step: 'financing',
      message: `Aforo (LTC) al ${aforo.toFixed(0)}% es atípicamente alto. Bancos típicos en MX: 60-70%.`,
    });
  }
  if (ltv > 0 && ltv < 40) {
    issues.push({
      severity: 'warning',
      step: 'financing',
      message: `LTV al ${ltv.toFixed(0)}% es muy conservador — puede sobre-restringir tus disposiciones.`,
    });
  }

  // ── TASA ──
  const rate = pct(a.loan_interest_rate_annual);
  if (creditLine > 0 && rate > 0 && (rate < 5 || rate > 25)) {
    issues.push({
      severity: 'warning',
      step: 'financing',
      message: `Tasa anual del crédito en ${rate.toFixed(1)}% — típico en MX: 10-16%. Verifica que no incluyas la tasa mensual.`,
    });
  }

  // ── HORIZONTE vs PLAZO CRÉDITO ──
  // SWA-157 · sin corrida, el piso: presale + construction + post del escenario.
  const horizon = Number(runHorizon)
    || Number(a.presale_months || 0) + Number(a.construction_months || 0) + Number(a.post_construction_months || 0);
  const loanTerm = Number(a.loan_term_months || 0);
  if (loanTerm > 0 && horizon > 0 && loanTerm > horizon) {
    issues.push({
      severity: 'info',
      step: 'financing',
      message: `El plazo del crédito (${loanTerm}m) supera el horizonte del proyecto (${horizon}m). El motor extenderá el horizonte automáticamente.`,
    });
  }

  // ── TIPOLOGÍAS ──
  // SWA-247B · `t.units` no existe en el payload: el campo es `quantity`
  // (steps.jsx:176, :300). Con el nombre equivocado esta suma era 0 y la regla
  // NUNCA disparaba, así que el sello decía "Saludable · Sin observaciones"
  // sobre escenarios donde el motor sí repartía las unidades.
  const tipologias = structured?.typologies || [];
  const unitsFromTipologias = tipologias.reduce((s, t) => s + (Number(t.quantity) || 0), 0);
  const totalUnits = Number(a.total_units || 0);
  // Umbral idéntico al del motor: UNA unidad entera · orchestrator.py:181
  // (`abs(user_units - total_q) >= 1`). Es el mismo punto en que el motor
  // activa el reparto proporcional de SWA-206, así que el semáforo no tolera
  // nada que el motor no tolere, ni avisa de lo que el motor deja pasar.
  if (totalUnits > 0 && unitsFromTipologias > 0 && Math.abs(unitsFromTipologias - totalUnits) >= 1) {
    issues.push({
      severity: 'warning',
      step: 'typologies',
      message: `Unidades totales (${totalUnits}) ≠ suma de tipologías (${unitsFromTipologias}). El motor usará ${totalUnits} y repartirá esa cantidad entre las tipologías en proporción a sus cifras actuales. Ajusta una u otra para que el reparto sea el que quieres.`,
    });
  }
  if (totalUnits > 0 && tipologias.length === 0) {
    issues.push({
      severity: 'info',
      step: 'typologies',
      message: 'No hay tipologías definidas. Se usará precio promedio y hard cost simplificado.',
    });
  }

  // ── ABSORCIÓN ──
  const abs = Number(a.absorption_rate_units_per_month || 0);
  if (abs > 0 && totalUnits > 0) {
    const monthsToSell = totalUnits / abs;
    if (monthsToSell > 60) {
      issues.push({
        severity: 'warning',
        step: 'sales',
        message: `A ${abs.toFixed(1)} uds/mes tardarás ~${monthsToSell.toFixed(0)} meses en vender todo. ¿Realista para tu mercado?`,
      });
    }
  }

  // ── COSTO / PRECIO ──
  const priceAvg = Number(a.avg_price_per_unit || 0);
  const hardCostAvg = Number(a.hard_cost_per_unit || 0);
  if (priceAvg > 0 && hardCostAvg > 0 && hardCostAvg / priceAvg > 0.75) {
    issues.push({
      severity: 'warning',
      step: 'budget',
      message: `Costo duro es ${((hardCostAvg / priceAvg) * 100).toFixed(0)}% del precio. Margen bruto podría ser insuficiente (típico <60%).`,
    });
  }

  // ── LAND ──
  const landCost = Number(a.land_cost || 0);
  const landArea = Number(a.total_land_area_m2 || 0);
  if (landCost === 0 && landArea > 0) {
    issues.push({
      severity: 'info',
      step: 'land',
      message: 'Costo del terreno en $0 — asegúrate si es aporte del socio o terreno propio (no confundir con costo cero real).',
    });
  }

  // SWA-237 · el semáforo debe ver lo mismo que verá el reporte del motor.
  issues.push(...buildCaptureIssues(assumptions, structured));

  return issues;
}

function computeHealthScore(issues, assumptions = {}, structured = {}) {
  let score = 100;
  issues.forEach((iss) => {
    if (iss.severity === 'critical') score -= 25;
    else if (iss.kind === 'captura') score -= 12;
    else if (iss.severity === 'warning') score -= 8;
    else if (iss.severity === 'info') score -= 2;
  });

  // Bonos por completitud
  const required = [
    'project_type', 'housing_format', 'market_segment',
    'total_units', 'avg_price_per_unit', 'construction_months',
  ];
  const filled = required.filter((k) => {
    const v = assumptions?.[k];
    return v !== undefined && v !== null && v !== '' && v !== 0;
  }).length;
  const fillPenalty = (required.length - filled) * 5;
  score -= fillPenalty;

  // SWA-237 · con captura abierta el score no puede alcanzar el umbral de
  // "Saludable" (80): el análisis correría sobre una base incompleta.
  const capped = issues.some((i) => i.kind === 'captura') ? Math.min(score, 79) : score;

  return Math.max(0, Math.min(100, Math.round(capped)));
}

const ICONS = {
  critical: AlertTriangle,
  warning: AlertCircle,
  info: Info,
};

const STYLES = {
  critical: { bg: '#FEF2F2', border: '#FCA5A5', text: '#991B1B' },
  warning: { bg: '#FFFBEB', border: '#FCD34D', text: '#92400E' },
  info: { bg: '#EFF6FF', border: '#93C5FD', text: '#1E40AF' },
};

// SWA-237 · captura abierta: naranja propio, distinto del ámbar de supuestos.
const CAPTURE_STYLE = { bg: '#FFF7ED', border: '#FDBA74', text: '#9A3412' };

export function WizardValidationBanner({ assumptions, structured, onGoToStep }) {
  const runHorizon = useRunHorizon();
  const issues = useMemo(() => buildIssues(assumptions, structured, runHorizon), [assumptions, structured, runHorizon]);

  if (issues.length === 0) {
    return (
      <div className="rounded-xl px-4 py-2.5 flex items-center gap-2.5" style={{ background: '#F0FDF4', border: '1px solid #86EFAC' }} data-testid="wizard-validation-ok">
        <CheckCircle2 className="h-4 w-4 shrink-0" style={{ color: '#16A34A' }} />
        <span className="text-xs font-medium" style={{ color: '#166534' }}>
          Escenario coherente. Sin advertencias detectadas.
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-1.5" data-testid="wizard-validation-list">
      {issues.slice(0, 4).map((iss, idx) => {
        const Icon = ICONS[iss.severity];
        const st = STYLES[iss.severity];
        return (
          <button
            key={idx}
            onClick={() => onGoToStep?.(iss.step)}
            className="w-full rounded-xl px-3 py-2 flex items-start gap-2.5 text-left transition-all hover:opacity-90"
            style={{ background: st.bg, border: `1px solid ${st.border}` }}
            data-testid={`wizard-validation-issue-${iss.severity}`}
          >
            <Icon className="h-4 w-4 shrink-0 mt-0.5" style={{ color: st.text }} />
            <span className="text-xs" style={{ color: st.text }}>{iss.message}</span>
          </button>
        );
      })}
      {issues.length > 4 && (
        <div className="text-xs text-muted-foreground text-center pt-1">
          + {issues.length - 4} advertencia{issues.length - 4 > 1 ? 's' : ''} adicionales
        </div>
      )}
    </div>
  );
}

export function WizardHealthScore({ assumptions, structured, onGoToStep }) {
  const [open, setOpen] = useState(false);
  const runHorizon = useRunHorizon();
  const issues = useMemo(() => buildIssues(assumptions, structured, runHorizon), [assumptions, structured, runHorizon]);
  const score = useMemo(() => computeHealthScore(issues, assumptions, structured), [issues, assumptions, structured]);

  // SWA-237 · precedencia critical > captura > warning > info.
  // "Datos incompletos" (captura) y "Revisar" (supuestos) son problemas
  // distintos y no se arreglan igual, por eso son estados distintos.
  const criticals = issues.filter((i) => i.severity === 'critical');
  const captures = issues.filter((i) => i.kind === 'captura');
  const warnings = issues.filter((i) => i.severity === 'warning' && i.kind !== 'captura');
  const infos = issues.filter((i) => i.severity === 'info');

  let label, detail, color, bg;
  if (criticals.length > 0) {
    label = 'Crítico';
    detail = `${criticals.length} crítico${criticals.length > 1 ? 's' : ''}`;
    color = '#DC2626'; bg = '#FEF2F2';
  } else if (captures.length > 0) {
    label = 'Datos incompletos';
    detail = `${captures.length} eje${captures.length > 1 ? 's' : ''} sin cerrar`;
    color = '#EA580C'; bg = '#FFF7ED';
  } else if (warnings.length > 0) {
    label = 'Revisar';
    detail = `${warnings.length} advertencia${warnings.length > 1 ? 's' : ''}`;
    color = '#F59E0B'; bg = '#FFFBEB';
  } else {
    label = 'Saludable';
    detail = infos.length > 0 ? `${infos.length} nota${infos.length > 1 ? 's' : ''}` : 'Sin observaciones';
    color = '#16A34A'; bg = '#F0FDF4';
  }

  // Orden del popover: lo que invalida el número, primero.
  const ordered = [...criticals, ...captures, ...warnings, ...infos];

  const trigger = (
    <div className="rounded-xl px-3 py-2 flex items-center gap-3 transition-all" style={{ background: bg, border: `1px solid ${color}30` }} data-testid="wizard-health-score">
      <div className="flex items-center gap-2">
        <div className="relative w-10 h-10">
          <svg viewBox="0 0 36 36" className="w-full h-full transform -rotate-90">
            <path
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              stroke="#E5E7EB"
              strokeWidth="3"
            />
            <path
              d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              fill="none"
              stroke={color}
              strokeWidth="3"
              strokeDasharray={`${score}, 100`}
              strokeLinecap="round"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center text-xs font-bold tabular-nums" style={{ color }}>
            {score}
          </div>
        </div>
        <div className="text-left">
          <div className="text-xs font-semibold" style={{ color }} data-testid="wizard-health-label">Salud: {label}</div>
          <div className="text-[10px] text-muted-foreground" data-testid="wizard-health-detail">{detail}</div>
        </div>
      </div>
    </div>
  );

  if (ordered.length === 0) return trigger;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className="rounded-xl hover:opacity-90 transition-opacity" data-testid="wizard-health-trigger" title="Ver observaciones del escenario">
          {trigger}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[420px] p-0" data-testid="wizard-health-popover">
        <div className="px-3 py-2 border-b border-border">
          <div className="text-xs font-semibold" style={{ color }}>Salud: {label} — {detail}</div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            Revisión previa a la corrida. Haz clic en una observación para ir a su paso.
          </div>
        </div>
        <div className="max-h-[320px] overflow-y-auto p-2 space-y-1.5">
          {ordered.map((iss, idx) => {
            const st = iss.kind === 'captura' ? CAPTURE_STYLE : STYLES[iss.severity];
            const Icon = iss.kind === 'captura' ? AlertCircle : ICONS[iss.severity];
            return (
              <button
                key={idx}
                onClick={() => { setOpen(false); onGoToStep?.(iss.step); }}
                className="w-full rounded-lg px-2.5 py-2 flex items-start gap-2 text-left transition-all hover:opacity-90"
                style={{ background: st.bg, border: `1px solid ${st.border}` }}
                data-testid={`wizard-health-issue-${iss.kind === 'captura' ? 'captura' : iss.severity}`}
              >
                <Icon className="h-3.5 w-3.5 shrink-0 mt-0.5" style={{ color: st.text }} />
                <span className="text-[11px] leading-snug" style={{ color: st.text }}>{iss.message}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { buildIssues, computeHealthScore };
